import { useTranslation } from "react-i18next";

/**
 * Review block for an OPEN (free-text) answer. Shows the student's typed answer, the teacher's
 * reference answer, the AI correctness score (colour-coded) or its grading status, and — for a
 * teacher within the review window — Approve / Reject buttons.
 *
 * Colours follow the spec: < 50 red, 50-70 orange, 70-90 yellow, > 90 green (and > 90 is the
 * auto-correct threshold, decided server-side).
 */
const chip = { display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600 };
const btn = { padding: "6px 14px", borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: "pointer", border: "none" };
const boxStyle = { whiteSpace: "pre-wrap", wordBreak: "break-word", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 4, padding: "10px 12px", fontSize: 14, lineHeight: 1.5, marginTop: 4 };
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.3 };

const scoreColor = (score) => {
    if (score == null) return { bg: "#f1f5f9", fg: "#475569", border: "#e2e8f0" };
    if (score < 50) return { bg: "#fee2e2", fg: "#b91c1c", border: "#fecaca" };   // red
    if (score < 70) return { bg: "#ffedd5", fg: "#c2410c", border: "#fed7aa" };   // orange
    if (score <= 90) return { bg: "#fef9c3", fg: "#854d0e", border: "#fde68a" };  // yellow
    return { bg: "#dcfce7", fg: "#15803d", border: "#bbf7d0" };                   // green (> 90)
};

const OpenAnswerReview = ({ question, canGrade = false, acting = false, selfView = false, onApprove, onReject }) => {
    const { t } = useTranslation();
    const q = question || {};
    const status = q.gradeStatus;
    const score = typeof q.aiScore === "number" ? q.aiScore : null;
    const pending = status === "PENDING_AI";
    const failed = status === "AI_FAILED";
    const manual = status === "MANUAL_APPROVED" || status === "MANUAL_REJECTED";
    const c = scoreColor(score);
    const answerLabel = selfView
        ? t("pages.examAttemptDetail.yourAnswerLabel")
        : t("pages.examAttemptDetail.studentAnswer");

    return (
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={labelStyle}>{t("pages.examAttemptDetail.aiEvaluation")}</span>
                {pending && (
                    <span style={{ ...chip, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" }}>
                        {t("pages.examAttemptDetail.aiPending")}
                    </span>
                )}
                {failed && (
                    <span style={{ ...chip, background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" }}>
                        {t("pages.examAttemptDetail.aiNeedsManual")}
                    </span>
                )}
                {score != null && (
                    <span style={{ ...chip, background: c.bg, color: c.fg, border: `1px solid ${c.border}` }}>
                        {t("pages.examAttemptDetail.aiScore", { score })}
                    </span>
                )}
                {!pending && !failed && (
                    <span
                        style={{
                            ...chip,
                            ...(q.correct
                                ? { background: "#dcfce7", color: "#15803d", border: "1px solid #bbf7d0" }
                                : { background: "#fee2e2", color: "#b91c1c", border: "1px solid #fecaca" }),
                        }}
                    >
                        {q.correct ? t("pages.examAttemptDetail.markedCorrect") : t("pages.examAttemptDetail.markedIncorrect")}
                        {manual ? ` · ${t("pages.examAttemptDetail.byTeacher")}` : ""}
                    </span>
                )}
            </div>

            <div>
                <div style={labelStyle}>{answerLabel}</div>
                <div style={boxStyle}>
                    {q.studentTextAnswer && q.studentTextAnswer.trim()
                        ? q.studentTextAnswer
                        : <span style={{ color: "#94a3b8" }}>{t("pages.examAttemptDetail.noAnswerGiven")}</span>}
                </div>
            </div>

            {q.correctAnswer && (
                <div>
                    <div style={labelStyle}>{t("pages.examAttemptDetail.referenceAnswer")}</div>
                    <div style={{ ...boxStyle, background: "#f0fdf4", border: "1px solid #bbf7d0" }}>{q.correctAnswer}</div>
                </div>
            )}

            {q.aiFeedback && (
                <div style={{ fontSize: 13, color: "#475569", fontStyle: "italic" }}>
                    {t("pages.examAttemptDetail.aiFeedback")}: {q.aiFeedback}
                </div>
            )}

            {canGrade && !manual && (
                <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" onClick={onApprove} disabled={acting} style={{ ...btn, background: "#16a34a", color: "#fff", opacity: acting ? 0.6 : 1 }}>
                        {t("pages.examAttemptDetail.approveAnswer")}
                    </button>
                    <button type="button" onClick={onReject} disabled={acting} style={{ ...btn, background: "#fff", color: "#dc2626", border: "1px solid #dc2626", opacity: acting ? 0.6 : 1 }}>
                        {t("pages.examAttemptDetail.rejectAnswer")}
                    </button>
                </div>
            )}
        </div>
    );
};

export default OpenAnswerReview;
